import styles from './AnimatedBackground.module.css';

/**
 * 富动态艺术背景 — 极光旋转层 + 4 色弥散光团流动 + 点阵网格 +
 * 6 条渐变数据流线 + 12 颗漂浮知识粒子 + AI 节点网络脉冲 +
 * 浮动几何形状 + 扩散涟漪 + 批阅符号水印。
 * 纯 CSS 动画，不拦截任何交互。
 */
export function AnimatedBackground() {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: `${(i * 8.3 + 4) % 100}%`,
    delay: `${-(i * 1.7)}s`,
    duration: `${16 + (i % 4) * 6}s`,
    colorClass:
      i % 3 === 0 ? styles.particleBlue : i % 3 === 1 ? styles.particleCyan : styles.particlePurple,
  }));

  return (
    <div className={styles.bg} aria-hidden="true">
      {/* 第一层：缓慢旋转的锥形极光渐变 */}
      <div className={styles.aurora} />

      {/* 第二层：4 个不同颜色/位置/节奏的弥散光团 */}
      <div className={`${styles.blob} ${styles.blobBlue}`} />
      <div className={`${styles.blob} ${styles.blobCyan}`} />
      <div className={`${styles.blob} ${styles.blobPurple}`} />
      <div className={`${styles.blob} ${styles.blobTeal}`} />

      {/* 第三层：全页面极淡点阵网格肌理 */}
      <div className={styles.dots} />

      {/* 第四层：6 条渐变描边数据流线（缓慢横向流动） */}
      <svg className={styles.flow} viewBox="0 0 1200 800" preserveAspectRatio="none">
        <defs>
          <linearGradient id="flowGradBlue" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(22,93,255,0)" />
            <stop offset="50%" stopColor="rgba(22,93,255,0.15)" />
            <stop offset="100%" stopColor="rgba(34,211,238,0)" />
          </linearGradient>
          <linearGradient id="flowGradPurple" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(139,92,246,0)" />
            <stop offset="50%" stopColor="rgba(139,92,246,0.12)" />
            <stop offset="100%" stopColor="rgba(22,93,255,0)" />
          </linearGradient>
          <linearGradient id="flowGradTeal" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(20,184,166,0)" />
            <stop offset="50%" stopColor="rgba(20,184,166,0.1)" />
            <stop offset="100%" stopColor="rgba(139,92,246,0)" />
          </linearGradient>
        </defs>
        <path className={styles.flowLine} stroke="url(#flowGradBlue)" d="M-50 100 C 250 40, 520 160, 820 90 S 1300 50, 1320 110" />
        <path className={`${styles.flowLine} ${styles.flowSlow}`} stroke="url(#flowGradPurple)" d="M-50 250 C 280 190, 540 310, 860 240 S 1300 200, 1320 270" />
        <path className={styles.flowLine} stroke="url(#flowGradBlue)" d="M-50 400 C 260 340, 560 440, 880 370 S 1300 330, 1320 400" />
        <path className={`${styles.flowLine} ${styles.flowSlow}`} stroke="url(#flowGradTeal)" d="M-50 550 C 290 480, 570 600, 890 530 S 1300 490, 1320 560" />
        <path className={styles.flowLine} stroke="url(#flowGradBlue)" d="M-50 680 C 270 620, 540 730, 850 660 S 1300 620, 1320 690" />
        <path className={`${styles.flowLine} ${styles.flowSlow}`} stroke="url(#flowGradPurple)" d="M-50 50 C 200 0, 500 120, 800 60 S 1280 20, 1320 70" />
      </svg>

      {/* 第五层：12 颗漂浮知识粒子（3 色，从底部缓缓上浮 + 水平摆动） */}
      <div className={styles.particles}>
        {particles.map((p) => (
          <span
            key={p.id}
            className={`${styles.particle} ${p.colorClass}`}
            style={{
              left: p.left,
              animationDelay: p.delay,
              animationDuration: p.duration,
            }}
          />
        ))}
      </div>

      {/* 第六层：右下角 AI 节点网络（节点呼吸 + 连线虚线流动 + 脉冲光点沿路径行进） */}
      <svg className={styles.net} viewBox="0 0 420 320">
        <g className={styles.netLines} stroke="rgba(22,93,255,0.5)" strokeWidth="1" fill="none">
          <path d="M40 250 L120 180 L210 210 L300 130 L380 190" />
          <path d="M120 180 L170 90 L300 130" />
          <path d="M210 210 L260 280 L380 190" />
          <path d="M40 250 L100 300 L170 90" />
          <path d="M300 130 L350 60" />
          <path d="M40 250 L210 210" />
          <path d="M170 90 L380 190" />
        </g>
        {/* 沿网络连线行进的脉冲光点（3 种颜色，不同路径/节奏） */}
        <g className={styles.netPulses}>
          <circle className={styles.pulse} r="2.5" fill="#22d3ee">
            <animateMotion dur="4s" repeatCount="indefinite" path="M40 250 L120 180 L210 210 L300 130 L380 190" />
          </circle>
          <circle className={styles.pulse} r="2" fill="#8b5cf6">
            <animateMotion dur="3.5s" repeatCount="indefinite" begin="1s" path="M120 180 L170 90 L300 130" />
          </circle>
          <circle className={styles.pulse} r="2" fill="#165dff">
            <animateMotion dur="5s" repeatCount="indefinite" begin="2s" path="M210 210 L260 280 L380 190" />
          </circle>
          <circle className={styles.pulse} r="1.8" fill="#14b8a6">
            <animateMotion dur="4.5s" repeatCount="indefinite" begin="0.5s" path="M40 250 L210 210" />
          </circle>
        </g>
        <g className={styles.netNodes} fill="#165dff">
          <circle className={styles.node} cx="40" cy="250" r="5" />
          <circle className={styles.node} cx="120" cy="180" r="6" />
          <circle className={styles.node} cx="210" cy="210" r="5" />
          <circle className={styles.node} cx="300" cy="130" r="7" />
          <circle className={styles.node} cx="380" cy="190" r="5" />
          <circle className={styles.node} cx="170" cy="90" r="6" />
          <circle className={styles.node} cx="260" cy="280" r="5" />
          <circle className={styles.node} cx="100" cy="300" r="4" />
          <circle className={styles.node} cx="350" cy="60" r="5" />
        </g>
      </svg>

      {/* 第七层：浮动几何形状（圆/环/六边形，缓慢旋转+上下浮动） */}
      <div className={styles.shapes}>
        <div className={`${styles.shape} ${styles.shapeCircle}`} />
        <div className={`${styles.shape} ${styles.shapeRing}`} />
        <div className={`${styles.shape} ${styles.shapeHex}`} />
      </div>

      {/* 第八层：涟漪扩散（2 组，缓慢扩大后消失循环） */}
      <div className={styles.ripples}>
        <div className={`${styles.ripple} ${styles.ripple1}`} />
        <div className={`${styles.ripple} ${styles.ripple2}`} />
      </div>

      {/* 第九层：极淡批阅符号水印（对勾/铅笔/波浪批改） */}
      <svg className={styles.marks} viewBox="0 0 200 200" fill="none">
        <path
          d="M40 110 L72 142 L128 78"
          stroke="rgba(22,93,255,0.55)"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M150 60 L165 120 L150 130 L132 75 Z"
          stroke="rgba(245,63,63,0.5)"
          strokeWidth="4"
          strokeLinejoin="round"
        />
        <path
          d="M60 165 q12 -14 24 0 t24 0 t24 0"
          stroke="rgba(22,93,255,0.45)"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}
